import type { CallbackInterface } from 'recoil'

import type { ModalBulkUnsubscribeState } from 'Pages/RecipientsList/model'

import { appliedSearchFiltersState } from 'Pages/RecipientsList/recoil/state/filters'
import {
  modalBulkUnsubscribeCanWipeDataState,
  modalBulkUnsubscribeState,
} from 'Pages/RecipientsList/recoil/state/modals'
import {
  selectionCountState,
  selectionState,
} from 'Pages/RecipientsList/recoil/state/results'

import { modalUnsubscribeOpenCallback } from 'Pages/RecipientsList/recoil/callbacks/modals'
import { updatePagePreferences } from 'Pages/RecipientsList/recoil/services/filters'
import {
  bulkUnsubscribe,
  notifyBulkUnsubscription,
} from 'Pages/RecipientsList/recoil/services/modalBulkUnsubscribe'

import type { PageStateType } from 'Types/state'

import { pageState } from 'State/page'

import { logAppError } from 'Helpers/logger'

import getIntl from 'Helpers/intl'

export const modalBulkUnsubscribeOpenCallback =
  ({ set, snapshot, ...rest }: CallbackInterface) =>
  async () => {
    const count = await snapshot.getPromise(selectionCountState)

    const { mode, checked } = await snapshot.getPromise(selectionState)

    if (mode === 'selection' && checked.length === 1) {
      void modalUnsubscribeOpenCallback({ set, snapshot, ...rest })(checked[0])
    } else if (count > 0) {
      set<ModalBulkUnsubscribeState>(modalBulkUnsubscribeState, (prev) => ({
        ...prev,
        show: true,
        wipeData: false,
      }))
    }
  }

export const modalBulkUnsubscribeToggleFieldCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async (field: 'channelEmail' | 'channelSMS' | 'wipeData') => {
    const canWipeData = await snapshot.getPromise(
      modalBulkUnsubscribeCanWipeDataState,
    )

    if (field === 'wipeData' && !canWipeData) {
      set<ModalBulkUnsubscribeState>(modalBulkUnsubscribeState, (prev) => ({
        ...prev,
        wipeData: false,
      }))
    } else {
      set<ModalBulkUnsubscribeState>(modalBulkUnsubscribeState, (prev) => ({
        ...prev,
        [field]: !prev[field],
      }))
    }
  }

export const modalBulkUnsubscribeCancelCallback =
  ({ set, reset }: CallbackInterface) =>
  async () => {
    set<ModalBulkUnsubscribeState>(modalBulkUnsubscribeState, (prev) => ({
      ...prev,
      show: false,
      wipeData: false,
    }))
  }

export const modalBulkUnsubscribeConfirmCallback =
  ({ set, snapshot, reset, ...cbi }: CallbackInterface) =>
  async () => {
    const { mode, checked } = await snapshot.getPromise(selectionState)

    const searchFilters = await snapshot.getPromise(appliedSearchFiltersState)

    const { channelEmail, channelSMS, wipeData } =
      await snapshot.getPromise<ModalBulkUnsubscribeState>(
        modalBulkUnsubscribeState,
      )

    set(pageState, (prev) => ({
      ...prev,
      loading: true,
    }))

    try {
      await bulkUnsubscribe(
        { mode, checked, searchFilters },
        { channelEmail, channelSMS, wipeData },
      )

      await updatePagePreferences({
        modalBulkUnsubscribe: {
          channelEmail,
          channelSMS,
        },
      })

      set(modalBulkUnsubscribeState, (prev) => ({
        ...prev,
        show: false,
        wipeData: false,
      }))

      set(pageState, (prev) => ({
        ...prev,
        loading: false,
      }))

      notifyBulkUnsubscription()
    } catch (error) {
      console.error('### error', error)

      logAppError({
        Component: 'RecipientsList',
        Method: 'modalBulkUnsubscribeConfirmCallback',
        appError: error,
      })

      set(modalBulkUnsubscribeState, (prev) => ({
        ...prev,
        show: false,
        wipeData: false,
      }))

      set<PageStateType>(pageState, (prev) => ({
        ...prev,
        status: 'error',
        loading: false,
        errorMessage: getIntl().formatMessage({
          id: 'Load.Alerts.ErrorGeneric',
        }),
      }))
    }
  }
